import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import QueryKeys from '@constants/queryKeys';
import { dismissNotification } from '@worker/services/inbox/api';
import type { OtherNotificationResponse } from '@th-types/worker-notifications.type';

type MutationContext = {
  previousQueries: Array<
    [readonly unknown[], OtherNotificationResponse | undefined]
  >;
};

export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) => dismissNotification(notificationId),
    onMutate: async (notificationId: number): Promise<MutationContext> => {
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.WorkerInbox.OTHER_NOTIFICATIONS],
      });

      const previousQueries =
        queryClient.getQueriesData<OtherNotificationResponse>({
          queryKey: [QueryKeys.WorkerInbox.OTHER_NOTIFICATIONS],
        });

      queryClient.setQueriesData<OtherNotificationResponse>(
        { queryKey: [QueryKeys.WorkerInbox.OTHER_NOTIFICATIONS] },
        (previousData) => {
          if (!previousData) return previousData;
          const notificationToDismiss = previousData.list.find(
            (notification) => notification.id === notificationId
          );
          const wasUnread = notificationToDismiss
            ? !notificationToDismiss.isRead
            : false;
          return {
            count: Math.max(0, previousData.count - 1),
            countNew: wasUnread
              ? Math.max(0, previousData.countNew - 1)
              : previousData.countNew,
            list: previousData.list.filter(
              (notification) => notification.id !== notificationId
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _id, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      notifications.show({
        color: 'red',
        title: 'Could not dismiss',
        message: 'Please try again.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.OTHER_NOTIFICATIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.INDICATORS],
      });
    },
  });
}
